/**
 * unsortedShare (#2005) — how much of the ledger is still in the ``unsorted``
 * bucket for a given set of months. All Kontoauszug-derived cash_flows land as
 * ``unsorted`` until a farmer categorises them; the Kategorien budget view
 * deliberately excludes the crosscutting group, so unsorted rows are otherwise
 * invisible. This drives the header chip that deep-links a farmer into the
 * Buchungen ledger's existing "kategorisieren" flow.
 *
 * Pure — groups by transaction (so a multi-record transaction counts once) and
 * uses the transaction's headline amount, exactly as the ledger row does.
 */
import { groupRecords, headRecord } from './transactions';
import { UNSORTED } from './ledgerCategory';
import type { LiquiditaetRecord } from './types';

export interface UnsortedShare {
  /** Number of still-unsorted transactions in scope. */
  count: number;
  /** Σ of their (sign-free) headline amounts, in EUR. */
  totalEur: number;
}

/**
 * The unsorted count + € across ``records``, restricted to ``months`` (a list of
 * ``YYYY-MM``; an empty list means all months). A transaction is unsorted when its
 * CANONICAL category (the ``groupRecords`` fold) is ``unsorted``.
 */
export function unsortedShare(records: LiquiditaetRecord[], months: string[]): UnsortedShare {
  const monthSet = months.length > 0 ? new Set(months) : null;
  let count = 0;
  let totalEur = 0;
  for (const tx of groupRecords(records)) {
    if (tx.ledger_category !== UNSORTED) continue;
    const head = headRecord(tx);
    const ym = head.period_ym || head.value_date.slice(0, 7);
    if (monthSet && !monthSet.has(ym)) continue;
    count += 1;
    totalEur += Math.abs(head.total_eur);
  }
  return { count, totalEur };
}
